import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const ADMIN_USERS_BASE = `${API_BASE}/admin/users`;

// Types
export interface UserAdmin {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  ico?: string;
  role: string;
  isActive: boolean;
  emailConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
  hasManagerProfile: boolean;
  apartmentCount: number;
  plannedApartmentCount?: number;
  preferredBillingCycle?: string;
  lastSeenAt?: string;
  /** Last successful login; absent until the user logs in after this was introduced (never inferred from lastSeenAt). */
  lastLoginAt?: string;
  lastFormVisitAt?: string;
  anonymized: boolean;
  /** Detail view only (absent in lists): the server's verdict on whether this user's password may be reset by the acting admin. */
  passwordResetAllowed?: boolean | null;
}

export interface UserDeletionImpact {
  apartments: number;
  reservations: number;
  billingRecords: number;
  contacts: number;
  cleaners: number;
  paymentMethods: number;
  pmsConnections: number;
  hasStripeAccount: boolean;
  anonymizeBlockedReason: string | null;
  purgeEnabled: boolean;
}

export interface ManagerAdmin {
  id: number;
  user: UserAdmin;
  isActive: boolean;
  billingStatus: string;
  apartmentCount: number;
  reservationCount: number;
  guestCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PageViewDay {
  date: string;
  count: number;
}

export interface PageViewStats {
  landing:  PageViewDay[];
  register: PageViewDay[];
  login:    PageViewDay[];
}

export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  managerUsers: number;
  totalManagers: number;
  activeManagers: number;
  totalApartments: number;
  activeApartments: number;
  totalReservations: number;
  activeReservations: number;
  totalGuests: number;
  pageViews: PageViewStats;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: string; // ADMIN, MANAGER, GUEST
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface ChangeRoleRequest {
  role: string;
}

export interface ApartmentCalendarInfo {
  id: number;
  name: string;
  url: string;
  source: string;
}

export interface ApartmentAdmin {
  id: number;
  name: string;
  address: string;
  capacity: number;
  isActive: boolean;
  ttlockEnabled?: boolean;
  createdAt?: string;
  reservationCount: number;
  calendarCount: number;
  guideEnabled: boolean;
  cleaningEnabled: boolean;
  calendars?: ApartmentCalendarInfo[];
}

// User Management APIs
export type UserSortField = 'name' | 'apartments' | 'lastLogin';
export type UserSortDir = 'asc' | 'desc';

export const getAllUsers = async (token: string, sort?: UserSortField, dir?: UserSortDir): Promise<UserAdmin[]> => {
  const res = await axios.get(ADMIN_USERS_BASE, {
    params: sort ? { sort, dir: dir || 'asc' } : undefined,
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getUserById = async (id: number, token: string): Promise<UserAdmin> => {
  const res = await axios.get(`${ADMIN_USERS_BASE}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const createUser = async (user: CreateUserRequest, token: string): Promise<UserAdmin> => {
  const res = await axios.post(ADMIN_USERS_BASE, user, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const updateUser = async (id: number, user: UpdateUserRequest, token: string): Promise<UserAdmin> => {
  const res = await axios.put(`${ADMIN_USERS_BASE}/${id}`, user, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getUserDeletionImpact = async (id: number, token: string): Promise<UserDeletionImpact> => {
  const res = await axios.get(`${ADMIN_USERS_BASE}/${id}/deletion-impact`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

/** Turns the admin "Delete everything" action on or off for this environment (off by default). */
export const setPurgeEnabled = async (enabled: boolean, token: string): Promise<void> => {
  await axios.put(`${API_BASE}/admin/safety-settings/purge`, { enabled }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

/** Wipes personal data, keeps invoices. */
export const anonymizeUser = async (id: number, token: string): Promise<void> => {
  await axios.delete(`${ADMIN_USERS_BASE}/${id}`, {
    params: { mode: 'ANONYMIZE' },
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

/** Deletes the user and everything they own, invoices included. The server re-checks `email`. */
export const purgeUser = async (id: number, email: string, token: string): Promise<void> => {
  await axios.delete(`${ADMIN_USERS_BASE}/${id}`, {
    params: { mode: 'PURGE', confirmEmail: email },
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const activateUser = async (id: number, token: string): Promise<UserAdmin> => {
  const res = await axios.post(`${ADMIN_USERS_BASE}/${id}/activate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const deactivateUser = async (id: number, token: string): Promise<UserAdmin> => {
  const res = await axios.post(`${ADMIN_USERS_BASE}/${id}/deactivate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const verifyUserEmail = async (id: number, token: string): Promise<UserAdmin> => {
  const res = await axios.post(`${ADMIN_USERS_BASE}/${id}/verify-email`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

/**
 * Admin sets another user's password directly. The password travels only in the POST body (never the
 * URL) and the server answers 204 with no body. Rejections carry { code, message } in the response.
 */
export const resetUserPassword = async (id: number, newPassword: string, token: string): Promise<void> => {
  await axios.post(`${ADMIN_USERS_BASE}/${id}/password-reset`, { newPassword }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const changeUserRole = async (id: number, role: ChangeRoleRequest, token: string): Promise<UserAdmin> => {
  const res = await axios.put(`${ADMIN_USERS_BASE}/${id}/role`, role, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// Manager Management APIs
export const getAllManagers = async (token: string): Promise<ManagerAdmin[]> => {
  const res = await axios.get(`${API_BASE}/admin/managers`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getManagerById = async (id: number, token: string): Promise<ManagerAdmin> => {
  const res = await axios.get(`${API_BASE}/admin/managers/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const activateManager = async (id: number, token: string): Promise<ManagerAdmin> => {
  const res = await axios.post(`${API_BASE}/admin/managers/${id}/activate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const deactivateManager = async (id: number, token: string): Promise<ManagerAdmin> => {
  const res = await axios.post(`${API_BASE}/admin/managers/${id}/deactivate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// System Statistics APIs
export const getSystemStats = async (token: string): Promise<SystemStats> => {
  const res = await axios.get(`${API_BASE}/admin/stats`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export interface MonthlyDayEntry {
  date: string;
  count: number;
}

export interface MonthlyStats {
  month: string;
  users: MonthlyDayEntry[];
  reservations: MonthlyDayEntry[];
  apartments: MonthlyDayEntry[];
  totalNewUsers: number;
  totalNewReservations: number;
  totalNewApartments: number;
}

export const getMonthlyStats = async (token: string, year?: number, month?: number): Promise<MonthlyStats> => {
  const params = year && month ? `?year=${year}&month=${month}` : '';
  const res = await axios.get(`${API_BASE}/admin/stats/monthly${params}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// Same shape as MonthlyStats, but for an arbitrary [start, end] window (yyyy-MM-dd, inclusive) —
// backs the dashboard's 7/30/90-day presets and its month picker alike.
export interface StatsRangeDayEntry {
  date: string;
  count: number;
}

export interface StatsRange {
  start: string;
  end: string;
  users: StatsRangeDayEntry[];
  reservations: StatsRangeDayEntry[];
  apartments: StatsRangeDayEntry[];
  totalNewUsers: number;
  totalNewReservations: number;
  totalNewApartments: number;
  pageViews: PageViewStats;
}

export const getStatsRange = async (token: string, start: string, end: string): Promise<StatsRange> => {
  const res = await axios.get(`${API_BASE}/admin/stats/range?start=${start}&end=${end}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// Admin apartment control APIs
export const getApartmentsByManager = async (managerId: number, token: string): Promise<ApartmentAdmin[]> => {
  const res = await axios.get(`${API_BASE}/admin/apartments/manager/${managerId}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const activateApartmentByAdmin = async (apartmentId: number, token: string): Promise<ApartmentAdmin> => {
  const res = await axios.post(`${API_BASE}/admin/apartments/${apartmentId}/activate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const deactivateApartmentByAdmin = async (apartmentId: number, token: string): Promise<ApartmentAdmin> => {
  const res = await axios.post(`${API_BASE}/admin/apartments/${apartmentId}/deactivate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const changeAdminPassword = async (newPassword: string, token: string): Promise<void> => {
  const res = await axios.post(`${API_BASE}/admin/change-password`, { newPassword }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export interface AdminSendEmailRequest {
  from?: string;
  to: string;
  subject: string;
  body: string;
}

export const sendAdminEmail = async (payload: AdminSendEmailRequest, token: string): Promise<{ success: boolean; message: string }> => {
  const res = await axios.post(`${API_BASE}/admin/email/send`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export interface ImpersonateResponse {
  token: string;
  email: string;
  role: string;
  name: string;
}

export const impersonateUser = async (userId: number, token: string): Promise<ImpersonateResponse> => {
  const res = await axios.post(`${API_BASE}/admin/user-management/${userId}/impersonate`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
